import { Fragment, RefObject, useEffect, useRef, useState } from "react"
import Head from "next/head"
import Image from "next/image"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useMediaQuery } from "@/hooks/use-media-query"
import MarketingLayout from "@/layouts/marketing"
import { PublicPageProps } from "@/types/page"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { CF_S3_URL } from "@/lib/cloudflare-loader"
import { MtSSP } from "@/lib/mt-server-side-props"
import { Container } from "@/components/container"
import IconArrowUp from "@/components/icons/arrow-up"
import { CompanyNav } from "@/components/marketing/company-nav"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"

export default function TeamPage({ session }: PublicPageProps) {
  const initUser = session?.user
  const init = useRef<boolean>(false)
  const topRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!init.current) {
      init.current = true
      sendAnalyticsEvent("ViewTeamPage")
    }
  }, [])

  const [selectedId, setSelectedId] = useState<string | undefined>()
  const isXS = useMediaQuery(MEDIA_QUERIES.xsMax)

  useEffect(() => {
    const handleScroll = () => {
      const listItems = document.querySelectorAll(".js-member")
      const scrollY = window.scrollY
      let currentId = undefined

      listItems.forEach((item) => {
        const { top } = item.getBoundingClientRect()
        const itemTop = top + scrollY - 320
        if (scrollY >= itemTop) {
          currentId = item.id
        }
      })

      if (currentId !== selectedId) {
        setSelectedId(currentId)
      }
    }

    if (isXS) {
      window.addEventListener("scroll", handleScroll)
    }

    return () => {
      if (isXS) {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  }, [selectedId, isXS])

  return (
    <MarketingLayout initUser={initUser}>
      <Head>
        <title>About Mindtrip: Meet our founders and leadership team.</title>
        <meta
          name="description"
          content="Meet the masterminds behind Mindtrip. Formerly of Apple, Google and more, they’re leveraging new technology to make travel planning as fun as the trip itself."
        />
      </Head>
      <div className="flex h-105 flex-col justify-center overflow-hidden rounded-b-4xl bg-[#FFBD00] sm:h-150 sm:rounded-b-7xl">
        <Container>
          <div className="grid items-center lg:grid-cols-2">
            <h1 className="font-circular text-7xl sm:text-9xl">Team</h1>
            <div className="hidden lg:block">
              <Image
                src={`${CF_S3_URL}/web/mindtripmagic.png`}
                alt=""
                width={374}
                height={361}
                priority
                className="mx-auto -mb-20"
              />
            </div>
          </div>
        </Container>
      </div>
      <div ref={topRef} className="relative -top-16" />
      <CompanyNav />
      <Container className="pb-24">
        <div className="flex justify-center">
          <svg
            width="50"
            height="50"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.08"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.9708 31.0979C21.3505 31.274 20.6978 31.3036 20.064 31.1846C19.4303 31.0655 18.8329 30.801 18.3187 30.4119C17.8046 30.0227 17.3877 29.5196 17.1011 28.942C16.8144 28.3644 16.6657 27.7281 16.6666 27.0833V19.7917C16.6666 18.6866 17.1056 17.6268 17.887 16.8454C18.6684 16.064 19.7282 15.625 20.8333 15.625C21.9384 15.625 22.9982 16.064 23.7796 16.8454C24.561 17.6268 25 18.6866 25 19.7917V26.1729" />
            <path d="M25 26.0417V19.7917C25 18.6866 25.439 17.6268 26.2204 16.8454C27.0018 16.064 28.0616 15.625 29.1667 15.625C30.2717 15.625 31.3315 16.064 32.1129 16.8454C32.8943 17.6268 33.3333 18.6866 33.3333 19.7917V26.0417" />
            <path d="M41.6667 33.7042V5.20832C41.6667 4.10325 41.2277 3.04345 40.4463 2.26204C39.6649 1.48064 38.6051 1.04166 37.5 1.04166C36.395 1.04166 35.3352 1.48064 34.5538 2.26204C33.7724 3.04345 33.3334 4.10325 33.3334 5.20832V19.7917" />
            <path d="M25 42.7083C25 40.4982 25.878 38.3786 27.4408 36.8158C29.0036 35.253 31.1232 34.375 33.3334 34.375H26.0417C24.9366 34.375 23.8768 33.936 23.0954 33.1546C22.314 32.3732 21.875 31.3134 21.875 30.2083C21.875 29.1033 22.314 28.0435 23.0954 27.2621C23.8768 26.4807 24.9366 26.0417 26.0417 26.0417H33.3917C35.1668 26.0425 36.8965 26.6023 38.3355 27.6417C39.3273 28.3201 40.1457 29.2223 40.7244 30.2755C41.3031 31.3287 41.6259 32.5032 41.6667 33.7042C41.6667 40.2313 35.0938 48.9583 25 48.9583C20.5798 48.9583 16.3405 47.2024 13.2149 44.0768C10.0893 40.9512 8.33337 36.712 8.33337 32.2917V9.37501C8.33337 8.26994 8.77236 7.21013 9.55376 6.42873C10.3352 5.64733 11.395 5.20834 12.5 5.20834C13.6051 5.20834 14.6649 5.64733 15.4463 6.42873C16.2277 7.21013 16.6667 8.26994 16.6667 9.37501V19.7917" />
          </svg>
        </div>

        <h2 className="mt-9 mb-12 text-center text-5xl font-semibold">
          The Masterminds Behind Mindtrip
        </h2>

        <h3 className="text-center text-2xl font-semibold">Leadership</h3>

        <ul className="mx-auto flex max-w-3xl flex-wrap items-baseline justify-center gap-x-5 leading-tight text-pretty *:w-36">
          {LEADERSHIP_TEAM.map((member, idx) => (
            <TeamItem
              key={member.first_name}
              index={idx}
              member={member}
              topRef={topRef}
            />
          ))}
        </ul>

        <h3 className="mt-16 text-center text-2xl font-semibold">Team</h3>

        <ul className="mx-auto flex max-w-3xl flex-wrap items-baseline justify-center gap-x-5 leading-tight text-pretty *:w-36">
          {TEAM_NON_LEADERSHIP.map((member, idx) => (
            <TeamItem
              key={member.first_name}
              index={idx}
              member={member}
              topRef={topRef}
            />
          ))}
        </ul>

        <h2 className="mt-24 mb-6 text-center text-2xl font-semibold sm:mt-36 sm:-mb-8">
          Leadership Bios
        </h2>

        <div className="sticky top-16 z-1 -mb-12 bg-background/80 backdrop-blur-md sm:hidden">
          <Select
            value={selectedId ?? ""}
            onValueChange={(value) => {
              if (!value) return
              const el = document.getElementById(value)
              el?.scrollIntoView({ behavior: "smooth" })
            }}
            items={{
              "": "Select",
              ...Object.fromEntries(
                LEADERSHIP_TEAM.map((m) => [
                  m.first_name,
                  `${m.first_name} ${m.last_name}`,
                ])
              ),
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEADERSHIP_TEAM.map((member) => (
                <SelectItem
                  key={member.first_name}
                  value={`${member.first_name}`}
                >
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ul className="mx-auto max-w-4xl">
          {LEADERSHIP_TEAM.map((member) => (
            <TeamItem key={member.first_name} member={member} topRef={topRef} />
          ))}
        </ul>
      </Container>
    </MarketingLayout>
  )
}

function TeamItem({
  member,
  index,
  topRef,
}: {
  member: TeamMember
  index?: number
  topRef: RefObject<HTMLDivElement | null>
}) {
  const slug = member.slug ?? member.first_name

  if (index !== undefined) {
    return (
      <Fragment key={slug}>
        <li className="mt-9">
          <a
            {...(member.leadership
              ? {
                  href: `#${member.first_name}`,
                  onClick: (e) => {
                    e.preventDefault()
                    const el = document.getElementById(member.first_name)
                    el?.scrollIntoView({ behavior: "smooth" })
                  },
                }
              : {
                  href: member.linkedin,
                  target: "_blank",
                  rel: "noopener noreferrer",
                })}
            className="block"
          >
            <Image
              src={`${CF_S3_URL}/web/${slug}.png`}
              width={128}
              height={159}
              alt=""
            />
            <div className="mt-4 text-sm leading-tight font-semibold">
              {member.first_name} {member.last_name}
            </div>
            <Separator className="my-2.5" />
            <div className="text-2xs font-medium text-muted">
              {member.title}
            </div>
          </a>
        </li>
        {index % 3 === 2 && (
          <li
            aria-hidden
            className="hidden basis-full sm:block [@media(min-width:1058px)]:hidden"
          />
        )}
      </Fragment>
    )
  } else {
    const btt = (
      <Button
        onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
        size="xs"
        variant="ghost"
        iconBefore={IconArrowUp}
        className="-ml-(--button-xs-px) text-muted hover:text-foreground"
      >
        Back to top
      </Button>
    )

    return (
      <li
        id={slug}
        className="js-member -mb-8 grid items-start pt-32 last:mb-0 sm:grid-cols-[1fr_15.875rem] sm:gap-9"
      >
        <div>
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-4 flex w-fit items-center gap-4"
          >
            <Image
              src={`${CF_S3_URL}/web/${slug}.png`}
              width={96}
              height={119}
              alt=""
              className="shrink-0"
            />
            <div className="w-44">
              <h3 className="text-lg leading-tight font-semibold">
                {member.first_name} {member.last_name}
              </h3>
              <Separator className="my-2.5" />
              <div className="text-2xs font-medium text-muted">
                {member.title}
              </div>
            </div>
          </a>
          <p className="max-w-prose leading-relaxed">{member.bio?.text}</p>
          <p className="mt-8 hidden sm:block">{btt}</p>
        </div>
        <div className="flex flex-col sm:mt-0">
          <Image
            src={`${CF_S3_URL}/web/${slug}_p.jpg`}
            width={229}
            height={120}
            alt=""
            className="mx-auto"
          />
          <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-black-a-7 bg-gray-2 p-5 text-xs text-pretty">
            <div>
              <h4 className="inline font-semibold">Based in:</h4>{" "}
              <p className="inline">{member.bio?.based}</p>
            </div>
            <div>
              <h4 className="inline font-semibold">
                Favorite place{" "}
                <span>{member.bio?.gender === "male" ? "he" : "she"}</span>’s
                traveled:
              </h4>{" "}
              <p className="inline">{member.bio?.favorite}</p>
            </div>
            <div>
              <h4 className="inline font-semibold">
                Weirdest souvenir{" "}
                <span>{member.bio?.gender === "male" ? "he" : "she"}</span>
                ’s ever brought back:
              </h4>{" "}
              <p className="inline">{member.bio?.souvenir}</p>
            </div>
            <div>
              <h4 className="inline font-semibold">
                On <span>{member.bio?.gender === "male" ? "his" : "her"}</span>{" "}
                bucket list:
              </h4>{" "}
              <p className="inline">{member.bio?.bucket}</p>
            </div>
          </div>
          <p className="mt-2.5 sm:hidden">{btt}</p>
        </div>
      </li>
    )
  }
}

export const getServerSideProps = MtSSP.publicPage()

type TeamMember = {
  slug?: string
  first_name: string
  last_name: string
  title: string
  linkedin: string
  bio?: {
    based: string
    text: string
    favorite: string
    souvenir: string
    bucket: string
    gender: "male" | "female"
  }
  leadership?: boolean
  founder?: boolean
}

const TEAM: TeamMember[] = [
  {
    first_name: "Andy",
    last_name: "Moss",
    title: "CEO, Founder",
    linkedin: "https://www.linkedin.com/in/andy-moss-7434a2",
    bio: {
      based: "San Francisco, CA",
      text: "Andy loves a good adventure — like traveling to Italy and Spain or starting new companies like Mindtrip. A serial entrepreneur, Andy has more than 20 years of expertise in launching and running successful online commerce companies. Prior to starting Mindtrip, Andy founded Roadster, an automotive retailing platform; FabKids, an online children’s apparel company; ShopStyle, the leading fashion search engine; and Cairo, an online service for retail discounts. He also held executive positions in business development at DemandTec and Ariba. Andy holds a Bachelor of Science degree from the University of Manchester, England.",
      favorite: "Tokyo and Kyoto in Japan",
      souvenir: "Very colorful Ken Done placemats from Australia",
      bucket: "Lord of the Rings trekking across the mountains in New Zealand",
      gender: "male",
    },
    leadership: true,
    founder: true,
  },
  {
    first_name: "Trey",
    last_name: "Matteson",
    title: "Engineering Leader, Founder",
    linkedin: "https://www.linkedin.com/in/treymatteson",
    bio: {
      based: "Palo Alto, CA",
      text: "Trey enjoys a variety of travel experiences — from hiking in Bhutan and Patagonia to taking in food and culture in New York and Paris. As an engineering leader, he is responsible for building Mindtrip’s AI and search core, as well as deployment and ops architecture. Prior to joining the team at Mindtrip, Trey was a co-founding engineer at Roadster and Popsugar’s ShopStyle. Before that, he applied his engineering skills at Ariba, NeXT and Apple. Trey holds a Bachelor of Science degree in math and computer science from Brown University.",
      favorite: "The Chomolhari Trek in Bhutan",
      souvenir: "A wine-tasting cup from Beaune",
      bucket: "New Zealand, Sicily and a hut-to-hut trek in the Alps",
      gender: "male",
    },
    leadership: true,
    founder: true,
  },
  {
    first_name: "Garrick",
    last_name: "Toubassi",
    title: "Engineering Leader, Founder",
    linkedin: "https://www.linkedin.com/in/gtoubassi",
    bio: {
      based: "Los Altos, CA",
      text: "Garrick likes experiencing nature’s grandeur while traveling in style. At Mindtrip, he plays psychotherapist to the LLM. Prior to Mindtrip, Garrick led engineering teams at major tech companies like Stripe and Google, where he led the development of Gmail, Calendar and Google Meet.  He also has experience in early-stage startups, including Ariba and ShopStyle. Garrick earned a Bachelor of Science degree in computer engineering from the University of Arizona.",
      favorite: "Dubrovnik",
      souvenir: "Cured reindeer meat from Stockholm",
      bucket: "Fiji and Greece",
      gender: "male",
    },
    leadership: true,
    founder: true,
  },
  {
    first_name: "Rudi",
    last_name: "Thun",
    title: "Chief Operating Officer",
    linkedin: "https://www.linkedin.com/in/rudithun",
    bio: {
      based: "San Francisco, CA",
      text: "Rudi loves off-the-beaten-path travel. When he’s not managing operations at Mindtrip, he’s out seeing the world and exploring new cultures. Before joining the Mindtrip team, Rudi was COO at Roadster and at Carwoo! He was also Head of Vehicles for eBay Motors, and VP and General Manager of AOL’s automotive properties. He earned a Bachelor of Science degree in mechanical engineering from Cornell University and an MBA from UC Berkeley.",
      favorite: "Bhutan and the glorious Himalayan Mountains",
      souvenir:
        "A huge bone spur from walking the Camino Frances (500 miles in 34 days!)",
      bucket: "Exploring every nook and cranny of Sardinia, Italy",
      gender: "male",
    },
    leadership: true,
  },
  {
    first_name: "Michelle",
    last_name: "Denogean",
    title: "Chief Marketing Officer",
    linkedin: "https://www.linkedin.com/in/michelledenogean",
    bio: {
      based: "Los Angeles, CA",
      text: "Michelle is an adventure seeker. When she’s not cycling through Lisbon or zip-lining through Maui, she’s in charge of everything Marketing at Mindtrip. Before joining the Mindtrip team, she developed the brand and go-to-market strategies as CMO at Side, a real estate brokerage platform; Roadster an automotive retailing platform; and Edmunds, a leading automotive marketplace. Prior to that,  Michelle held executive level roles in marketing and product leadership at eHarmony and Move.com. She is an alumna of UC San Diego and holds an MBA from Pepperdine University.",
      favorite: "Bad Gastein in the Austrian Alps",
      souvenir:
        "Not the weirdest, but I fell in love with a necklace while honeymooning in Florence, Italy. I didn’t buy it on the spot, but my husband had it shipped to me as an anniversary present one year later.",
      bucket: "Exploring Australia’s big cities, coastal regions and outback",
      gender: "female",
    },
    leadership: true,
  },
  {
    first_name: "Tom",
    last_name: "Krackeler",
    title: "Chief Product Officer",
    linkedin: "https://www.linkedin.com/in/tomkrackeler",
    bio: {
      based: "Berkeley, CA",
      text: "Tom’s favorite mode of travel is by foot, whether it’s exploring new cities or long distance hiking on the Appalachian, Pacific Crest and Continental Divide Trails. Before joining the Mindtrip team, Tom spent eight years at Zuora as Chief Customer Officer and SVP of Products. Prior to that, Tom was Co-Founder/CEO at Frontleaf, a customer analytics startup; and Co-Founder and SVP of Products at Convio and GetActive Software, which pioneered online fundraising for charities. He graduated from Duke University and earned an MPP from UC Berkeley.",
      favorite:
        "Backcountry camping on the John Muir Trail in Evolution Valley",
      souvenir: "Esoteric T-shirts",
      bucket: "Patagonia",
      gender: "male",
    },
    leadership: true,
  },
  {
    first_name: "Abby",
    last_name: "West",
    title: "VP Product",
    linkedin: "https://www.linkedin.com/in/abby-west-9060884b",
    bio: {
      based: "San Francisco, CA",
      text: "Abby loves travel that feels far removed from everyday life, with a mix of big nature and buzzy cities. At Mindtrip, she focuses on building features and AI agents that support travelers from early inspiration through the on-trip experience. Prior to Mindtrip, Abby held product and operational roles at several early-stage startups, including Looker, later acquired by Google for $2.6 billion. She also co-founded Thatch, a platform for travel creators, which was acquired by Mindtrip in 2025. Abby holds a BA and MPH from Yale University.",
      favorite:
        "The Sacred Valley in Peru (Machu Picchu) and a multiweek train trip through Western China",
      souvenir: "Some really sharp handmade kitchen knives from Istanbul",
      bucket: "The Azores, Morocco and Basque Country, Spain",
      gender: "female",
    },
    leadership: true,
  },
  {
    first_name: "Emily",
    last_name: "Blythe",
    title: "Director, Implementation",
    linkedin: "https://www.linkedin.com/in/emily-blythe",
  },
  {
    first_name: "Robbie",
    last_name: "Bockholt",
    title: "Implementation and Success",
    linkedin: "https://www.linkedin.com/in/robbiebockholt",
  },
  {
    first_name: "Jordan",
    last_name: "Castillo",
    title: "Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/jordancastillo",
    bio: {
      based: "Brooklyn, NY",
      text: "Jordan enjoys exploring music and art scenes, and unearthing local food and brewery gems while traveling. He’s thrilled to be a part of a team that’s redefining the travel experience. In his role, Jordan is responsible for Mindtrip’s transportation search, booking, and generative AI. Before that, he collaborated with teams at Roadster, Tesla, and Bolster, creating comprehensive solutions in cybersecurity, ecommerce, and DevOps tooling. Jordan holds a bachelor’s degree in computer engineering from Wichita State University.",
      favorite: "New York, NY",
      souvenir:
        "Unique collectibles including vinyl records, VHS tapes and classic video games.",
      bucket: "Commercial space travel",
      gender: "male",
    },
  },
  {
    first_name: "Jennifer",
    last_name: "Chen",
    title: "Implementation and Success",
    linkedin: "https://www.linkedin.com/in/jennifer-chen-520755295",
  },
  {
    first_name: "Chris",
    last_name: "Ciccone",
    title: "Director, Knowledge & Content",
    linkedin: "https://www.linkedin.com/in/christopher-ciccone",
  },
  {
    first_name: "Olivia",
    last_name: "Coning",
    title: "Social Media Manager",
    linkedin: "https://www.linkedin.com/in/olivia-coning-75593a12a",
  },
  {
    first_name: "Rob",
    last_name: "Davis",
    title: "Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/davisre",
    bio: {
      based: "Chicago, IL",
      text: "Rob loves seeing the world and building technology for it. At Mindtrip he’s working on trip-planning tools and AI streaming. Previously, he co-founded and contributed to a number of successful startups, including Roadster, FabKids, iAmaze, Perka and Lighthouse Design. Prior to that,  he was an engineer at Sun Microsystems, where he contributed some of the earliest user interface components to the standard Java platform. Besides engineering, Rob has a background in film and worked as a critic — traveling to festivals, interviewing legendary filmmakers and absorbing cinema through bleary eyes. Rob has a degree in computer science from Purdue University.",
      favorite: "Paris (for a month!)",
      souvenir: "A black iron tea kettle he uses every single day",
      bucket: "Spain",
      gender: "male",
    },
    founder: true,
  },
  {
    first_name: "Manish",
    last_name: "Dubey",
    title: "Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/madubey",
    bio: {
      based: "Fremont, CA",
      text: "Manish has a passion for adventure travel and immersing himself in diverse cultures. His professional journey has led him to contribute to dynamic startups, including Mindtrip and Shopstyle. Prior to co-founding Mindtrip, Manish made significant impacts as an engineer at LinkedIn, where he contributed to the company’s successful IPO and user base expansion from 100 million to 1 billion. Manish has a master’s degree in computer science from Louisiana State University and a bachelor’s degree in electrical engineering from the University of Bombay.",
      favorite: "Guayana, for its unspoiled rugged jungles and rivers",
      souvenir:
        "A lung infection that nearly killed him after a jungle safari in Africa",
      bucket: "Snow leopard trekking in the Annapurna Mountain Range",
      gender: "male",
    },
    founder: true,
  },
  {
    first_name: "Alex",
    last_name: "Farmer",
    title: "General Manager, EMEA",
    linkedin: "https://www.linkedin.com/in/alexanderfarmer",
    // bio: {
    //   based: "London, UK",
    //   text: "Alex is an avid traveler who is passionate about the intersection of technology and travel, and how AI is reshaping the way people discover, plan and experience the world. At Mindtrip, he leads the company’s growth and expansion across Europe, the Middle East and Africa. A seasoned technology executive, Alex has spent his career helping startups and scale-ups build and grow high-performing commercial organizations, with particular expertise in travel technology and B2B SaaS. Before joining Mindtrip, he held senior commercial leadership roles at Nezasa and Cognite, spanning revenue, marketing, customer success and international expansion. Alex holds a Bachelor of Arts degree in political science from Tufts University.",
    //   favorite: "Kyrgyzstan, San Sebastian, or Svalbard",
    //   souvenir: "A 1 Turkmen Manat note — one of the rarest currencies in the world, figured it was worth keeping.",
    //   bucket: "Antarctica and Mongolia",
    //   gender: "male",
    // },
  },
  {
    first_name: "Gui",
    last_name: "Forget",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/guillaume-forget",
  },
  {
    first_name: "Samantha",
    last_name: "Gaspin",
    title: "Marketing Manager",
    linkedin: "https://www.linkedin.com/in/samantha-gaspin",
  },
  {
    first_name: "Jesse",
    last_name: "Hart",
    title: "Design Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/jesse-hart-29789015a",
    bio: {
      based: "Boulder, CO",
      text: "Jesse’s lifestyle and work embody a passion for exploration and innovation in travel technology. As a founding designer at Mindtrip, Jesse seamlessly integrates design and code to craft engaging user experiences. Prior to Mindtrip, Jesse consulted for high-profile clients, including Sephora, and several Bay Area startups. When he’s not hard at work, Jesse enjoys off-the-beaten-path travels with his wife and homeschooled sons. Jesse holds a Bachelor of Fine Arts degree in design from The Art Institute of Boston.",
      favorite: "Touring Italy for 3 months",
      souvenir: "Etna basalt",
      bucket: "New Zealand",
      gender: "male",
    },
    founder: true,
  },
  {
    first_name: "Hans",
    last_name: "Hyttinen",
    title: "iOS Engineer",
    linkedin: "https://www.linkedin.com/in/lumilux",
  },
  {
    first_name: "David",
    last_name: "Jedeikin",
    title: "iOS Engineer",
    linkedin: "https://www.linkedin.com/in/davidjedeikin",
  },
  {
    first_name: "Elsa",
    last_name: "Kawai",
    title: "Design Leader, Co-founder",
    linkedin: "https://www.linkedin.com/in/elsakawai",
    bio: {
      based: "Elk, CA",
      text: "Elsa has a deep-rooted passion for storytelling and believes in the transformative power of travel. At Mindtrip, she oversees the seamless integration of brand strategy, creative, product design and production. Prior to co-founding Mindtrip, Elsa co-founded Roadster and served as creative director at Yahoo, Wikia, ShopStyle and Popsugar. Her work has been featured in Airbnb, Refinery29 and Sunset Magazine, and has been recognized by Fast Company and even the late Queen Elizabeth. In addition to leading the creative team at Mindtrip, Elsa is the proprietor of unique accommodations that celebrate the natural beauty of Elk, CA, where she lives.",
      favorite: "Indonesia and Elk, CA",
      souvenir: "It’s a secret 😉",
      bucket: "All the hidden gems",
      gender: "female",
    },
    founder: true,
  },
  {
    first_name: "Mika",
    last_name: "Kozma",
    title: "Design Leader, Co-founder",
    linkedin: "https://www.linkedin.com/in/mika-kozma-2326924",
    bio: {
      based: "San Francisco, CA",
      text: "Mika is highly skilled at using design and technology to tell stories. At Mindtrip, he’s responsible for creating and executing on the product vision, and optimizing the user experience. His work is inspired by the possibility of changing what travel means and how planning it should feel. Previously, he was a co-founder and the product design director at Roadster. Prior to that, he applied his product design prowess at Affinity, Meta, Messenger, Fandom and Cisco.",
      favorite: "San Francisco, Madrid, Paris, Stockholm and Prague",
      souvenir: "A donkey",
      bucket: "Porto, Budapest, Tokyo and Cape Town",
      gender: "male",
    },
    founder: true,
  },
  {
    first_name: "Mike",
    last_name: "Lenz",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/mlenz",
  },
  {
    first_name: "Sophie",
    last_name: "Lenz",
    title: "Data Analyst",
    linkedin: "https://www.linkedin.com/in/sophie-lenz",
  },
  {
    first_name: "Paul",
    last_name: "Lestz",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/paullestz",
  },
  {
    first_name: "Vincent",
    last_name: "Lo",
    title: "Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/vincent-lo-387103",
    bio: {
      based: "San Francisco, CA",
      text: "Vincent is an avid world traveler who enjoys eating at the finest restaurants, Michelin or otherwise. When he’s not enjoying a great glass of wine, you can find him building Mindtrip’s architecture, personalization platform, remarketing platform, booking flows and document processing capabilities. Prior to Mindtrip, he was a co-founding engineer at Roadster and a co-founder and CTO at FabKids. Before that, Vincent was an early engineer and manager at Ariba, and served as a software architect at Apple. He holds a Bachelor of Science degree in computer science from Brown University.",
      favorite: "Santorini",
      souvenir: "Sorry, I only take beautiful memories home.",
      bucket: "Viewing a total solar eclipse",
      gender: "male",
    },
    founder: true,
  },
  {
    first_name: "Leah",
    last_name: "Moss",
    title: "Content Specialist",
    linkedin: "https://www.linkedin.com/in/leah-moss-41b958a2",
  },
  {
    first_name: "Robert",
    last_name: "Patterson",
    title: "SVP Tourism Partnerships",
    linkedin: "https://www.linkedin.com/in/robertscottpatterson",
  },
  {
    first_name: "Andrew",
    last_name: "Pollet",
    title: "Integrations Manager",
    linkedin: "https://www.linkedin.com/in/andrew-pollet-08bbaa208",
  },
  {
    slug: "AndyR",
    first_name: "Andy",
    last_name: "Rado",
    title: "Designer",
    linkedin: "https://www.linkedin.com/in/andyrado",
  },
  {
    slug: "MichaelR",
    first_name: "Michael",
    last_name: "Ru",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/ru-michael",
  },
  {
    first_name: "Josie",
    last_name: "Scollin",
    title: "Design Operations",
    linkedin: "https://www.linkedin.com/in/josephinefox",
  },
  {
    first_name: "Stephen",
    last_name: "Selke",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/stephenselke",
  },
  {
    first_name: "Ryan",
    last_name: "Shan",
    title: "Engineer",
    linkedin: "https://www.linkedin.com/in/theryanshan",
  },
  {
    first_name: "Carlos",
    last_name: "Silva",
    title: "Chief Information Security Officer & Engineer",
    linkedin: "https://www.linkedin.com/in/mrsilva",
  },
  {
    first_name: "Victoria",
    last_name: "Urso",
    title: "Marketing Manager",
    linkedin: "https://www.linkedin.com/in/victoria-urso",
  },
  {
    first_name: "Jon",
    last_name: "Wall",
    title: "iOS Engineer",
    linkedin: "https://www.linkedin.com/in/jon-wall-5a74874",
  },
  {
    first_name: "Katherine",
    last_name: "Wang",
    title: "Engineer, Co-founder",
    linkedin: "https://www.linkedin.com/in/kuanchenkatherinewang",
    bio: {
      based: "Orange County, CA",
      text: "At Mindtrip, Katherine gets to pair two of her biggest passions: engineering and exploration. When she’s not using Generative AI to enrich travel content and build itineraries, she can be found in parks and museums with her children. Prior to teaming up with Mindtrip, she worked as an engineer, building and maintaining vital systems at Roadster and at AutoGravity, a Daimler Company backed startup. Katherine holds a master’s degree in computer science from UCLA, under the tutelage of Mario Gerla.",
      favorite: "Backpacking through Europe with her father for a month",
      souvenir: "A chopstick-and-bowl set",
      bucket: "Korea, Cambodia and India",
      gender: "female",
    },
    founder: true,
  },
]

export const LEADERSHIP_TEAM = TEAM.filter((person) => person.leadership)
export const FOUNDING_TEAM = TEAM.filter((person) => person.founder)
const TEAM_NON_LEADERSHIP = TEAM.filter((person) => !person.leadership)
